import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { clsx } from "clsx";
import { Button, Input } from "../ui";
import { useSession } from "../app/session";
import { LanguageSwitch } from "../app/LanguageSwitch";
import { giveBack } from "./device";

interface PublicProfile {
  id: string;
  name: string;
  color: string | null;
  kind: string;
  hasPin: boolean;
  /** an admin with a password (made by the installer) is asked for it instead of a PIN */
  hasPassword?: boolean;
  isAdmin: boolean;
}

const tone: Record<string, string> = {
  accent: "bg-accent",
  ok: "bg-ok",
  warn: "bg-warn",
  err: "bg-err",
  neutral: "bg-neutral-500",
};

/**
 * The profile picker `/who` (spec §9.29): names with colours, big tap targets, optional PIN. No email,
 * no username, no signup. An admin with a password asks for it; any other profile for its PIN, when it has one.
 */
export function WhoPage() {
  const { t } = useTranslation();
  const lending = useSession((s) => Boolean(s.session?.lending));
  const device = useSession((s) => s.session?.device);
  const load = useSession((s) => s.load);
  const sso = useSession((s) => Boolean(s.session?.login?.oidc));
  // a single sign-on that did not work comes back here (ADR 0023)
  const loginError = new URLSearchParams(location.search).get("login_error");
  const [circleName, setCircleName] = useState("");
  const [profiles, setProfiles] = useState<PublicProfile[]>([]);
  const [picked, setPicked] = useState<PublicProfile | null>(null);
  const [secret, setSecret] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void fetch("/api/internal/circle/profiles")
      .then((r) => r.json())
      .then((b: { circleName: string; profiles: PublicProfile[] }) => {
        setCircleName(b.circleName);
        setProfiles(b.profiles);
      });
  }, []);

  async function enter(p: PublicProfile, value?: string) {
    setError(null);
    const body: Record<string, string> = { profileId: p.id };
    if (value) body[p.hasPassword ? "password" : "pin"] = value;
    const res = await fetch("/api/internal/circle/who", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (res.ok) {
      await load();
      return;
    }
    setError(((await res.json()) as { error: string }).error);
  }

  const pick = (p: PublicProfile) => {
    setSecret("");
    setError(null);
    if (p.hasPin || p.hasPassword) setPicked(p);
    else void enter(p);
  };

  return (
    <div
      className="mx-auto flex min-h-dvh w-full max-w-2xl flex-col justify-center gap-8 px-6 py-10"
      data-testid="who"
    >
      <div className="flex items-center gap-2.5">
        <span className="grid h-7 w-7 place-items-center rounded-[7px] bg-accent text-[13px] font-bold tracking-[0.02em] text-white">
          Dr
        </span>
        <h1 className="text-[26px] font-semibold">{circleName || t("who.title")}</h1>
        <span className="ml-auto">
          <LanguageSwitch />
        </span>
      </div>
      {lending && (
        <div
          className="flex flex-wrap items-center gap-3 rounded-md border border-neutral-200 p-3 text-[13.5px] dark:border-neutral-800"
          data-testid="who-lent"
        >
          <span>{t("who.lentBy", { name: device?.ownerName ?? "" })}</span>
          <Button variant="secondary" size="sm" className="ml-auto" onClick={() => void giveBack()}>
            {t("who.giveBackTo", { name: device?.ownerName ?? "" })}
          </Button>
        </div>
      )}
      {!picked ? (
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {profiles.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                onClick={() => pick(p)}
                data-testid="who-profile"
                data-name={p.name}
                className="kss-surface flex h-32 w-full flex-col items-center justify-center gap-2 text-[17px] font-semibold transition hover:border-neutral-700"
              >
                <span
                  className={clsx(
                    "grid h-12 w-12 place-items-center rounded-full text-[18px] text-white",
                    tone[p.color ?? "neutral"] ?? tone.neutral,
                  )}
                  aria-hidden
                >
                  {p.name.slice(0, 1).toUpperCase()}
                </span>
                {p.name}
                {p.kind === "viewer" && (
                  <span className="font-mono text-[11px] font-normal text-neutral-500">
                    {t("who.viewer")}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {!picked && sso && (
        <a
          href="/api/internal/oidc/login"
          data-testid="who-sso"
          className="mx-auto inline-flex h-9 items-center justify-center rounded-sm border border-neutral-300 bg-[var(--surface)] px-3.5 text-[13.5px] font-medium hover:border-neutral-700 hover:bg-neutral-100 dark:hover:bg-neutral-800"
        >
          {t("login.sso")}
        </a>
      )}
      {!picked && loginError && (
        <p role="alert" className="text-center text-[12.5px] text-err" data-testid="login-error">
          {t(`errors.${loginError}`, { defaultValue: t("error.generic") })}
        </p>
      )}
      {picked ? (
        <form
          className="kss-surface mx-auto flex w-full max-w-sm flex-col gap-3 p-6"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            void enter(picked, secret);
          }}
        >
          <h2 className="text-[17px] font-semibold">{picked.name}</h2>
          <label className="flex flex-col gap-1 text-[12.5px] text-neutral-500">
            {picked.hasPassword ? t("login.password") : t("who.pin")}
            <Input
              type="password"
              inputMode={picked.hasPassword ? undefined : "numeric"}
              autoFocus
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              data-testid="who-secret"
              autoComplete={picked.hasPassword ? "current-password" : "off"}
            />
          </label>
          {error && (
            <p role="alert" className="text-[12.5px] text-err">
              {t(`errors.${error}`, { defaultValue: t("error.generic") })}
            </p>
          )}
          <div className="flex justify-between">
            <Button type="button" variant="ghost" onClick={() => setPicked(null)}>
              {t("who.back")}
            </Button>
            <Button type="submit" disabled={!secret}>
              {t("who.enter")}
            </Button>
          </div>
        </form>
      ) : null}
    </div>
  );
}
